import { useCallback, useEffect, useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import styles from './PlayerReportFiltersForm.module.css'
import { DatePickerFormField } from '../../../common/components/Form/DatePickerFormField/DatePickerFormField'
import { useDebounce } from '../../../hooks/useDebounce'
import { areObjectsEqual } from '../../../util/functional'
import { ReportFilters } from '../../../contexts/ReportsContextProvider'
import { SelectFormField } from '../../../common/components/Form/SelectFormField/SelectFormField'
import { InputFormField } from '../../../common/components/Form/InputFormField/InputFormField'
import { useUser } from '../../../contexts/userContext'
import { SelectOption } from '../../../common/components/Select/Select'

type PlayerReportFiltersProps = {
  defaultFrom?: string
  onChange: (filters: Partial<ReportFilters>) => void
}

export const PlayerReportFiltersForm: React.FC<PlayerReportFiltersProps> = ({ defaultFrom, onChange }) => {
  const { t } = useTranslation()
  const { user } = useUser()

  const [filters, setFilters] = useState<ReportFilters>({ createdAtFrom: defaultFrom })
  const debouncedFilters = useDebounce(filters, 1_000)

  const initialValues: ReportFilters = {
    createdAtFrom: undefined,
    createdAtUntil: undefined,
    gameName: undefined,
    tag: undefined,
    playerEmail: undefined,
  }

  const tagOptions = useMemo((): SelectOption<number>[] => {
    return (
      user?.tags.map((tag) => {
        return {
          value: tag.id,
          label: tag.label,
        }
      }) ?? []
    )
  }, [user?.tags])

  useEffect(() => {
    onChange(debouncedFilters)
  }, [onChange, debouncedFilters])

  const voidEnter = useCallback((e: any) => {
    if (e.keyCode === 13) e.preventDefault()
  }, [])

  return (
    <Form<ReportFilters> initialValues={initialValues} onSubmit={onChange}>
      {({ values, handleSubmit }) => (
        <form onKeyDown={voidEnter} onSubmit={handleSubmit} className={styles.filtersContainer}>
          <InputFormField
            name='playerEmail'
            label={t('report.filter_label.player', 'Player')}
            placeholder={t('report.filter_label.player', 'Player')}
            srOnlyLabel
          />
          <SelectFormField<number>
            name='tag'
            options={tagOptions}
            label={t('report.filter_label.tag', 'Tag')}
            placeholder={t('report.filter_label.tag', 'Tag')}
            srOnlyLabel
            isClearable
            isSearchable
          />
          <InputFormField
            name='gameName'
            label={t('report.filter_label.game_name', 'Game name')}
            placeholder={t('report.filter_label.game_name', 'Game name')}
            srOnlyLabel
          />
          <DatePickerFormField
            name='createdAtFrom'
            label={t('report.filter_label.created_at_from', 'Created at (from)')}
            placeholder={t('report.filter_label.created_at_from', 'Created at (from)')}
            srOnlyLabel
            isClearable
            maxDate={values.createdAtUntil == null ? undefined : new Date(values.createdAtUntil)}
          />
          <DatePickerFormField
            name='createdAtUntil'
            label={t('report.filter_label.created_at_until', 'Created at (until)')}
            placeholder={t('report.filter_label.created_at_until', 'Created at (until)')}
            srOnlyLabel
            isClearable
            minDate={values.createdAtFrom == null ? undefined : new Date(values.createdAtFrom)}
          />

          <FormSpy
            onChange={(props) =>
              setFilters((prev) => (areObjectsEqual(prev ?? {}, props.values) ? prev : props.values))
            }
          />
        </form>
      )}
    </Form>
  )
}
